import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Field, TextInput } from "./kit";

export default function TrustedByManager() {
  const [logos, setLogos] = useState([]);
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/admin/trusted-logos").then(({ data }) => setLogos(data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setLogoUrl(data.url);
      toast.success("Logo uploaded — press Add Brand");
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const add = async () => {
    if (!name.trim()) return toast.error("Brand name is required");
    await api.post("/admin/trusted-logos", { name: name.trim(), logo_url: logoUrl });
    setName("");
    setLogoUrl("");
    toast.success("Brand added — live on the site now");
    load();
  };

  const remove = async (l) => {
    await api.delete(`/admin/trusted-logos/${l.id}`);
    toast.success("Brand removed");
    load();
  };

  const move = async (l, dir) => {
    const idx = logos.findIndex((x) => x.id === l.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= logos.length) return;
    const next = [...logos];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setLogos(next);
    await api.post("/admin/trusted-logos/reorder", { ids: next.map((x) => x.id) });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-trusted-manager">
      <Card title="Trusted By Logos" desc="Brands in the scrolling strip on the site. Upload a logo (white or transparent PNG works best) or leave empty for a placeholder icon.">
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
          <Field label="Brand name" className="flex-1">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Brand One" />
          </Field>
          <div className="flex gap-3">
            <button className="btn btn-sm btn-outline-light" onClick={() => fileRef.current?.click()} data-testid="trusted-upload-button">
              <Upload size={14} /> Upload logo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            <button className="btn btn-sm btn-primary" onClick={add} data-testid="trusted-add-button">Add Brand</button>
          </div>
        </div>
        {logoUrl && (
          <div className="mt-4 flex items-center gap-3 text-xs text-cyan-300">
            <img src={logoUrl} alt="" className="h-8 w-auto" />
            <span>Logo ready — it will be used for the next brand you add</span>
          </div>
        )}
      </Card>

      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display font-bold text-white mb-4">Current brands ({logos.length})</h3>
        <div className="space-y-3">
          {logos.map((l) => (
            <div key={l.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-3" data-testid={`trusted-row-${l.id}`}>
              {l.logo_url ? (
                <img src={l.logo_url} alt="" className="h-8 w-auto" />
              ) : (
                <span className="w-8 h-8 rounded bg-white/10 flex items-center justify-center text-white text-xs font-bold">{(l.name || "?")[0]}</span>
              )}
              <div className="flex-1 font-semibold text-white text-sm">{l.name}</div>
              <button onClick={() => move(l, -1)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Move up"><ArrowUp size={15} /></button>
              <button onClick={() => move(l, 1)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Move down"><ArrowDown size={15} /></button>
              <button onClick={() => remove(l)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" aria-label="Remove brand" data-testid={`trusted-delete-${l.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
          {logos.length === 0 && <div className="py-6 text-center text-slate-500 text-sm">No brands yet — add the first one above.</div>}
        </div>
      </div>
    </div>
  );
}
